/* ──────────────────────────────────────────────────────────────────────────────
   Video Carousel behavior
   One class instance per section so multiple carousels on the same page don't
   collide. Initializes on DOMContentLoaded and re-initializes when the Shopify
   theme editor emits its `shopify:section:load` event (live preview).
   ──────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict'

  class VideoCarousel {
    constructor(root) {
      this.root = root
      this.track = root.querySelector('[data-track]')
      this.viewport = root.querySelector('[data-viewport]')
      this.slides = Array.from(root.querySelectorAll('[data-slide]'))
      this.arrows = {
        prev: root.querySelector('[data-direction="prev"]'),
        next: root.querySelector('[data-direction="next"]'),
      }
      this.dots = root.querySelector('[data-dots]')
      this.progress = root.querySelector('[data-progress] .video-carousel__progress-fill')
      this.playMode = root.dataset.playMode || 'click_to_play'
      this.autoplayTrigger = root.dataset.autoplayTrigger || 'on_scroll'
      this.alwaysMuted = root.dataset.alwaysMuted === 'true'

      this.bindCards()
      this.bindArrows()
      this.buildDots()
      this.bindScroll()

      if (this.playMode === 'autoplay') {
        this.setupAutoplay()
      }
    }

    /* ── Per-card playback (click-to-play, embed swap, mute toggle) ────── */
    bindCards() {
      const HIDDEN = 'video-carousel__icon-hidden'
      // Class-based toggle is required because the native `hidden` HTML attribute
      // can be overridden by theme CSS (e.g. Dawn declares `svg { display: ... }`
      // at higher specificity), which would cause both icons to render at once.
      const show = (el) => { if (el) el.classList.remove(HIDDEN) }
      const hide = (el) => { if (el) el.classList.add(HIDDEN) }

      this.slides.forEach((slide) => {
        const card = slide.querySelector('[data-card]')
        const media = slide.querySelector('[data-media]')
        const video = slide.querySelector('[data-video]')
        const embedPlaceholder = slide.querySelector('[data-embed-url]')
        const playButton = slide.querySelector('[data-play-button]')
        const pauseButton = slide.querySelector('[data-pause-button]')
        const muteToggle = slide.querySelector('[data-mute-toggle]')

        // When the card is wrapped in an <a href>, ANY click that bubbles up triggers
        // navigation. preventDefault alone doesn't stop bubbling — we also need to
        // stopPropagation so button clicks never reach the anchor handler. Applied
        // to all three media-overlay buttons below.
        const stopNav = (e) => { e.preventDefault(); e.stopPropagation() }

        // Initial play — reveals the native video (or the YouTube/Vimeo iframe).
        const startPlayback = (e) => {
          stopNav(e)
          if (video) {
            if (!this.alwaysMuted) video.muted = false
            video.play().catch((err) => console.warn('[video-carousel] video.play() failed:', err))
            card.classList.add('is-playing')
          } else if (embedPlaceholder) {
            this.swapForEmbed(embedPlaceholder)
            card.classList.add('is-playing')
          }
        }
        if (playButton) playButton.addEventListener('click', startPlayback)
        // Click-to-play only: entire media area is a hit target so viewers don't
        // have to aim for the small play icon. Ignored once playing so clicks on
        // hover controls aren't hijacked.
        if (this.playMode === 'click_to_play' && media) {
          media.addEventListener('click', (e) => {
            if (card.classList.contains('is-playing')) return
            if (e.target.closest('[data-pause-button], [data-mute-toggle]')) return
            startPlayback(e)
          })
        }

        // Pause / resume — shows on hover while playing. Toggles video state and
        // swaps the icon from pause ↔ play accordingly.
        if (pauseButton && video) {
          const pauseIcon = pauseButton.querySelector('[data-pause-icon]')
          const resumeIcon = pauseButton.querySelector('[data-resume-icon]')
          const syncPause = () => {
            if (video.paused) {
              hide(pauseIcon); show(resumeIcon)
              pauseButton.setAttribute('aria-label', 'Resume')
            } else {
              show(pauseIcon); hide(resumeIcon)
              pauseButton.setAttribute('aria-label', 'Pause')
            }
          }
          pauseButton.addEventListener('click', (e) => {
            stopNav(e)
            if (video.paused) video.play().catch(() => {})
            else video.pause()
          })
          video.addEventListener('play', syncPause)
          video.addEventListener('pause', syncPause)
          syncPause()
        }

        // Mute toggle (available in both modes, surfaced via hover).
        if (muteToggle && video) {
          const mutedIcon = muteToggle.querySelector('[data-mute-icon-muted]')
          const unmutedIcon = muteToggle.querySelector('[data-mute-icon-unmuted]')
          const syncMute = () => {
            if (video.muted) {
              show(mutedIcon); hide(unmutedIcon)
              muteToggle.setAttribute('aria-label', 'Unmute')
            } else {
              hide(mutedIcon); show(unmutedIcon)
              muteToggle.setAttribute('aria-label', 'Mute')
            }
          }
          muteToggle.addEventListener('click', (e) => {
            stopNav(e)
            // Unmute this video, mute all others (one audio source at a time).
            this.slides.forEach((s) => {
              const v = s.querySelector('[data-video]')
              if (v && v !== video) v.muted = true
            })
            video.muted = !video.muted
          })
          // React to any mute change — including the initial unmute inside
          // startPlayback — so the icon never drifts out of sync with reality.
          video.addEventListener('volumechange', syncMute)
          syncMute()
        }
      })
    }

    /** Replace a YouTube/Vimeo placeholder div with an autoplaying iframe. */
    swapForEmbed(placeholder) {
      const url = placeholder.dataset.embedUrl
      const iframeSrc = this.buildEmbedSrc(url)
      if (!iframeSrc) return
      const iframe = document.createElement('iframe')
      iframe.src = iframeSrc
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen')
      iframe.setAttribute('allowfullscreen', '')
      iframe.setAttribute('frameborder', '0')
      iframe.style.width = '100%'
      iframe.style.height = '100%'
      placeholder.innerHTML = ''
      placeholder.appendChild(iframe)
      placeholder.style.backgroundImage = ''
    }

    /** Build an autoplay-enabled embed URL from a YouTube or Vimeo share URL. */
    buildEmbedSrc(url) {
      try {
        const u = new URL(url)
        // YouTube (youtu.be + youtube.com watch + youtube.com/shorts)
        if (u.hostname.includes('youtu')) {
          let id
          if (u.hostname === 'youtu.be') id = u.pathname.slice(1)
          else if (u.pathname.startsWith('/shorts/')) id = u.pathname.split('/')[2]
          else id = u.searchParams.get('v')
          if (!id) return null
          const params = new URLSearchParams({
            autoplay: '1',
            rel: '0',
            modestbranding: '1',
            mute: this.alwaysMuted ? '1' : '0',
          })
          return `https://www.youtube.com/embed/${id}?${params.toString()}`
        }
        // Vimeo
        if (u.hostname.includes('vimeo')) {
          const id = u.pathname.split('/').filter(Boolean).pop()
          if (!id) return null
          const params = new URLSearchParams({
            autoplay: '1',
            muted: this.alwaysMuted ? '1' : '0',
            byline: '0',
            title: '0',
          })
          return `https://player.vimeo.com/video/${id}?${params.toString()}`
        }
      } catch (_) {}
      return null
    }

    /* ── Arrow nav (desktop) ───────────────────────────────────────────── */
    bindArrows() {
      if (this.arrows.prev) this.arrows.prev.addEventListener('click', () => this.nudge(-1))
      if (this.arrows.next) this.arrows.next.addEventListener('click', () => this.nudge(1))
    }

    nudge(direction) {
      if (!this.track) return
      const card = this.slides[0]
      if (!card) return
      const cardWidth = card.getBoundingClientRect().width
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0)
      this.track.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' })
    }

    /* ── Pagination dots ───────────────────────────────────────────────── */
    buildDots() {
      if (!this.dots) return
      this.dots.innerHTML = ''
      this.slides.forEach((_, i) => {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'video-carousel__dot'
        btn.setAttribute('role', 'tab')
        btn.setAttribute('aria-label', `Go to video ${i + 1}`)
        btn.addEventListener('click', () => {
          const card = this.slides[i]
          if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
        })
        this.dots.appendChild(btn)
      })
      this.updatePagination()
    }

    /* ── Scroll sync: keep dots + progress bar in sync with track scroll ─ */
    bindScroll() {
      if (!this.track) return
      let raf = 0
      this.track.addEventListener('scroll', () => {
        if (raf) cancelAnimationFrame(raf)
        raf = requestAnimationFrame(() => this.updatePagination())
      }, { passive: true })
      window.addEventListener('resize', () => this.updatePagination(), { passive: true })
    }

    updatePagination() {
      if (!this.track) return
      const maxScroll = this.track.scrollWidth - this.track.clientWidth
      const ratio = maxScroll > 0 ? this.track.scrollLeft / maxScroll : 0
      if (this.progress) this.progress.style.width = `${Math.min(1, ratio) * 100}%`

      if (this.dots) {
        const dotEls = this.dots.querySelectorAll('.video-carousel__dot')
        if (!dotEls.length) return
        const card = this.slides[0]
        if (!card) return
        const cardWidth = card.getBoundingClientRect().width
        const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0)
        const step = cardWidth + gap || 1
        const activeIdx = Math.round(this.track.scrollLeft / step)
        dotEls.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIdx))
      }
    }

    /* ── Autoplay (muted, on scroll into view) ─────────────────────────── */
    setupAutoplay() {
      if (typeof IntersectionObserver === 'undefined') {
        // Fallback: just try to play everything.
        this.slides.forEach((slide) => {
          const v = slide.querySelector('[data-video]')
          if (v) { v.muted = true; v.play().catch(() => {}) }
        })
        return
      }

      const obs = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('[data-video]')
          if (!video) return
          if (entry.isIntersecting) {
            video.muted = true
            video.play().catch(() => {
              // Some browsers refuse autoplay even muted in low-power mode. Silent fail.
            })
          } else {
            video.pause()
          }
        })
      }, { threshold: 0.5 })

      this.slides.forEach((slide) => obs.observe(slide))

      // "on_load" mode: just kick off playback immediately rather than waiting
      // for scroll. Useful for hero placements where the carousel is above the fold.
      if (this.autoplayTrigger === 'on_load') {
        this.slides.forEach((slide) => {
          const v = slide.querySelector('[data-video]')
          if (v) { v.muted = true; v.play().catch(() => {}) }
        })
      }
    }
  }

  function init(root = document) {
    root.querySelectorAll('.video-carousel').forEach((el) => {
      if (el.__videoCarousel) return // don't double-init on soft refreshes
      el.__videoCarousel = new VideoCarousel(el)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init())
  } else {
    init()
  }

  // Shopify theme editor re-renders sections on setting changes. Re-init when
  // our section reloads so new controls take effect without a full page refresh.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.classList.contains('video-carousel')) {
      e.target.__videoCarousel = null
      init(e.target.parentNode || document)
    }
  })
})()